import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ExprCall } from '@mode-switch/express';
import { Names, Values } from '@mode-switch/model';
import { EncChannel } from '@mode-vegas/vegas';
import { trackByFactory } from '@mode/capra';
import { ChartTypes, DataViewTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledAttr, Switch } from '@mode/shared/util-switch';
import { DataViewFacade } from '@mode/switch-views/contract';
import { filter, take } from 'rxjs/operators';
import { isPresent } from '@mode/shared/util-js';

export interface DragData {
  $field: CompiledAttr;
  $source: EncChannel | 'field-list';
  $valid: boolean;
}

export interface CalcModalEventObj {
  switchViewToken: string;
  viewName: string;
  viewMode?: 'static' | 'edit';
  calc?: FlamingoTypes.ModeCalc;
  readOnly?: boolean;
  duplicate?: boolean;
}

@Component({
  selector: 'mode-field-list',
  templateUrl: './field-list.component.html',
  styleUrls: ['./field-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldListComponent implements OnInit, OnChanges {
  @Input() view!: FlamingoTypes.ModeView;
  @Input() readOnly = false;
  @Input() chartObjectType: ChartTypes.ObjectType = ChartTypes.ObjectType.Visualization;
  @Input() switchViewToken = '';
  @Input() chartType: ChartTypes.ChartType = 'vegas';
  @Input() queryRunToken = '';

  @Output() dragStart = new EventEmitter<DragData>();
  @Output() dragEnd = new EventEmitter();
  @Output() triggerCalcModal = new EventEmitter<CalcModalEventObj>();
  @Output() triggerRemoveCalc = new EventEmitter<CalcModalEventObj>();
  @Output() applyFieldColor = new EventEmitter<{
    attr: FlamingoTypes.ModeAttr;
    color: ChartTypes.FieldColor;
  }>();
  @Output() applyFieldFormat = new EventEmitter<{
    attr: FlamingoTypes.ModeAttr;
    format: ChartTypes.TextFormat;
  }>();
  @Output() applyDefaultAgg = new EventEmitter<{
    attr: FlamingoTypes.ModeAttr;
    defaultAgg: ExprCall.Name;
  }>();

  measures: CompiledAttr[] = [];
  dimensions: CompiledAttr[] = [];
  originalMeasures: CompiledAttr[] = [];
  originalDimensions: CompiledAttr[] = [];
  attrs: ReadonlyArray<FlamingoTypes.ModeAttr> = [];
  valuesAttr!: CompiledAttr;
  namesAttr!: CompiledAttr;
  isFromImportedDataset = false;
  $attrId = trackByFactory<CompiledAttr>(
    (attr) => attr.attr.token ?? `${attr.attr.name}.${attr.dataType}.${attr.source}`
  );
  private columnNames: string[] = [];
  private parentCalcNames: string[] = [];
  private localCalcNames: string[] = [];
  private currentSearchTerm = '';

  constructor(private dataViewfacade: DataViewFacade) {}

  ngOnInit(): void {
    this.dataViewfacade
      .createSwitchViewBySwitchViewToken(this.switchViewToken)
      .pipe(take(1), filter(isPresent))
      .subscribe((switchView) => {
        this.isFromImportedDataset = switchView?.sourceType === DataViewTypes.SwitchViewSourceType.SwitchView;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['view'] || changes['chartType']) {
      this.measures = [];
      this.dimensions = [];

      if (this.view != null) {
        const model = Switch.Models.makeFromView(this.view.name, this.view);
        const sortByAttrName = (a1: CompiledAttr, a2: CompiledAttr): number => {
          if (!a1.attrName) {
            return -1;
          }
          if (!a2.attrName) {
            return 1;
          }
          return a1.attrName.localeCompare(a2.attrName);
        };
        this.measures = Switch.Attrs.measures(model, this.view.name)
          .map((attr) => CompiledAttr.fromAttr(this.view, attr))
          .sort(sortByAttrName);
        this.dimensions = Switch.Attrs.dimensions(model, this.view.name)
          .map((attr) => CompiledAttr.fromAttr(this.view, attr))
          .sort(sortByAttrName);

        this.valuesAttr = CompiledAttr.fromAttr(this.view, Values.make());
        this.namesAttr = CompiledAttr.fromAttr(this.view, Names.make());

        if (this.chartType === 'vegas') {
          this.measures.unshift(this.valuesAttr);
          this.dimensions.unshift(this.namesAttr);
        }

        this.originalMeasures = this.measures;
        this.originalDimensions = this.dimensions;

        this.onSearchTermChanged(this.currentSearchTerm);

        this.attrs = Switch.Attrs.getAll(model, this.view.name);
        const columnAttrs = Switch.Attrs.getColumns(model, this.view.name);
        const parentCalcAttrs = this.attrs.filter((attr) => Switch.Attrs.isParentCalc(attr));
        const localCalcAttrs = this.attrs.filter((attr) => Switch.Attrs.isOwnCalc(attr));
        this.columnNames = columnAttrs.map((attr) => attr.name);
        this.parentCalcNames = parentCalcAttrs.map((attr) => attr.name);
        this.localCalcNames = localCalcAttrs.map((attr) => attr.name);
      }
    }
  }

  matchesColumnName(attr: CompiledAttr): boolean {
    return !!attr.attrName && this.columnNames.includes(attr.attrName);
  }

  matchesParentCalcName(attr: CompiledAttr): boolean {
    return !!attr.attrName && this.parentCalcNames.includes(attr.attrName);
  }

  isCalc(attr: CompiledAttr): boolean {
    return attr.isCalc;
  }

  isLocalCalc(attr: CompiledAttr): boolean {
    return Switch.Attrs.isOwnCalc(attr.attr);
  }

  matchesLocalCalcName(attr: CompiledAttr): boolean {
    return !!attr.attrName && this.localCalcNames.includes(attr.attrName);
  }

  isColumn(attr: CompiledAttr): boolean {
    return attr.isColumn;
  }

  parentCalcHasDuplicateLocalCalcName(attr: CompiledAttr): boolean {
    const isExploreish = this.chartObjectType === ChartTypes.ObjectType.Exploration;
    return (
      isExploreish &&
      this.isCalc(attr) &&
      !this.isLocalCalc(attr) &&
      (this.matchesLocalCalcName(attr) || this.matchesColumnName(attr))
    );
  }

  localCalcDuplicatesColumnName(attr: CompiledAttr): boolean {
    return (
      this.isCalc(attr) && this.isLocalCalc(attr) && (this.matchesParentCalcName(attr) || this.matchesColumnName(attr))
    );
  }

  dragstart(ev: DragData, attr: CompiledAttr) {
    const isValid = this.isLocalCalc(attr)
      ? !this.localCalcDuplicatesColumnName(attr)
      : !this.parentCalcHasDuplicateLocalCalcName(attr);

    this.dragStart.emit({
      $field: attr,
      $source: 'field-list',
      $valid: isValid,
    });
  }

  dragend() {
    this.dragEnd.emit();
  }

  onCreateCalcField() {
    this.triggerCalcModal.emit({
      switchViewToken: this.switchViewToken,
      viewMode: 'edit',
      viewName: this.view.name,
      readOnly: false,
    });
  }

  onSearchTermChanged(searchTerm: string) {
    this.currentSearchTerm = searchTerm;
    if (searchTerm !== '') {
      this.measures = this.originalMeasures.filter((measure) => {
        return measure.displayName.toLowerCase().includes(searchTerm.toLowerCase());
      });
      this.dimensions = this.originalDimensions.filter((dimension) => {
        return dimension.displayName.toLowerCase().includes(searchTerm.toLowerCase());
      });
    } else {
      this.measures = this.originalMeasures;
      this.dimensions = this.originalDimensions;
    }
  }

  onEditCalcField(calc: FlamingoTypes.ModeCalc) {
    this.triggerCalcModal.emit({
      switchViewToken: this.switchViewToken,
      viewMode: 'edit',
      viewName: this.view.name,
      readOnly: false,
      calc,
    });
  }

  onViewCalcField(calc: FlamingoTypes.ModeCalc) {
    const readOnly =
      // The calc is explicitly NOT a local calc
      calc?.isLocal === false;

    this.triggerCalcModal.emit({
      switchViewToken: this.switchViewToken,
      viewName: this.view.name,
      calc,
      viewMode: 'static',
      readOnly,
    });
  }

  onDuplicateCalcField(calc: FlamingoTypes.ModeCalc) {
    const model = Switch.Models.makeFromView(this.view.name, this.view);
    const newCalc = Switch.Calcs.duplicate(model, this.view.name, calc);

    this.triggerCalcModal.emit({
      switchViewToken: this.switchViewToken,
      viewName: this.view.name,
      viewMode: 'edit',
      readOnly: false,
      calc: newCalc,
      duplicate: true,
    });
  }

  onRemoveCalcField(calc: FlamingoTypes.ModeCalc) {
    this.triggerRemoveCalc.emit({
      switchViewToken: this.switchViewToken,
      viewName: this.view.name,
      calc,
    });
  }

  onApplyFieldColor(event: { attr: FlamingoTypes.ModeAttr; color: ChartTypes.FieldColor }) {
    this.applyFieldColor.emit(event);
  }

  onApplyFieldFormat(event: { attr: FlamingoTypes.ModeAttr; format: ChartTypes.TextFormat }) {
    this.applyFieldFormat.emit(event);
  }

  onApplyDefaultAgg(event: { attr: FlamingoTypes.ModeAttr; defaultAgg: ExprCall.Name }) {
    this.applyDefaultAgg.emit(event);
  }
}
